import { useState, useEffect, useRef } from "react";
import { useAuth } from "./AuthorizationFetch";

/**
 * Custom hook for loading hierarchical dropdown data from the API
 *
 * This hook fetches and organizes organizational data in a nested structure:
 * Company → Teams & Departments → Divisions → Positions
 *
 * The resulting data structure allows for cascading dropdowns where:
 * - Selecting a company shows its teams and departments
 * - Selecting a department shows its divisions
 * - Selecting a division shows its positions
 *
 * @returns {Object} Hook return object
 * @returns {Object} dropdownData - Hierarchical data organized by company
 * @returns {string|null} error - Error message if any request fails
 * @returns {boolean} loading - Loading state indicator
 *
 * @example
 * const { dropdownData, error, loading } = useDropdownData();
 *
 * // Example data structure returned:
 * {
 *   "Company A": {
 *     id: 1,
 *     teams: [
 *       { id: 1, name: "Development Team" },
 *       { id: 2, name: "QA Team" }
 *     ],
 *     departments: [
 *       {
 *         id: 1,
 *         name: "Engineering",
 *         divisions: [
 *           {
 *             id: 1,
 *             name: "Frontend",
 *             positions: [
 *               { id: 1, name: "React Developer" },
 *               { id: 2, name: "UI/UX Designer" }
 *             ]
 *           }
 *         ]
 *       }
 *     ]
 *   }
 * }
 */
const useDropdownData = () => {
  const { authenticatedFetch } = useAuth();

  // Store authenticatedFetch in ref to prevent unnecessary re-renders
  const fetchRef = useRef(authenticatedFetch);

  // State management for dropdown data, errors, and loading
  const [dropdownData, setDropdownData] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isCancelled = false; // Cleanup flag to prevent state updates after unmount
    const BASE_URL = `${process.env.REACT_APP_BASE_URL}`;

    /**
     * Main function that orchestrates the fetching of all dropdown data
     *
     * Data fetching flow:
     * 1. Fetch all companies (top level)
     * 2. For each company:
     *    - Fetch teams associated with that company
     *    - Fetch departments associated with that company
     *    - For each department:
     *      - Fetch divisions within that department
     *      - For each division:
     *        - Fetch positions within that division
     *
     * This creates a fully nested data structure for hierarchical dropdowns
     */
    const fetchDropdownData = async () => {
      setLoading(true);
      try {
        // ====================================================================
        // STEP 1: Fetch all companies (root level of hierarchy)
        // ====================================================================
        const companiesResponse = await fetchRef.current(
          `${BASE_URL}/entities/companies`
        );
        if (!companiesResponse.ok) {
          throw new Error(
            `Request failed with status ${companiesResponse.status} on ${BASE_URL}/entities/companies`
          );
        }
        const companies = await companiesResponse.json();
        const data = {}; // Final data structure to be returned

        // ====================================================================
        // STEP 2: Process each company and build hierarchical structure
        // ====================================================================
        for (const company of companies) {
          const companyName = company.name;

          // Initialize company object with basic structure
          data[companyName] = {
            id: company.id,
            teams: [], // Teams directly under this company
            departments: [], // Departments with nested divisions and positions
          };

          // ================================================================
          // STEP 2A: Fetch teams for current company
          // ================================================================
          const teamsResponse = await fetchRef.current(
            `${BASE_URL}/entities/teams?companyId=${company.id}`
          );
          if (!teamsResponse.ok) {
            throw new Error(
              `Request failed with status ${teamsResponse.status} on /entities/teams?companyId=${company.id}`
            );
          }
          const teams = await teamsResponse.json();

          // Store teams in simplified format (id + name only)
          data[companyName].teams = teams.map((team) => ({
            id: team.id,
            name: team.name,
          }));

          // ================================================================
          // STEP 2B: Fetch departments for current company
          // ================================================================
          const departmentsResponse = await fetchRef.current(
            `${BASE_URL}/entities/departments?companyId=${company.id}`
          );
          if (!departmentsResponse.ok) {
            throw new Error(
              `Request failed with status ${departmentsResponse.status} on /entities/departments?companyId=${company.id}`
            );
          }
          const departments = await departmentsResponse.json();

          // ================================================================
          // STEP 2C: For each department, fetch its divisions and positions
          // ================================================================
          const departmentsWithDivisions = [];
          for (const department of departments) {
            // Fetch all divisions under this department
            const divisionsResponse = await fetchRef.current(
              `${BASE_URL}/entities/divisions?departamentId=${department.id}` // Note: API uses "departament" (typo)
            );
            if (!divisionsResponse.ok) {
              throw new Error(
                `Request failed with status ${divisionsResponse.status} on /entities/divisions?departamentId=${department.id}`
              );
            }
            const divisions = await divisionsResponse.json();

            // ============================================================
            // STEP 2D: For each division, fetch its positions
            // ============================================================
            const divisionsWithPositions = [];
            for (const division of divisions) {
              // Retrieve positions for each division
              const positionsResponse = await fetchRef.current(
                `${BASE_URL}/entities/positions?divisionId=${division.id}`
              );
              if (!positionsResponse.ok) {
                throw new Error(
                  `Request failed with status ${positionsResponse.status} on /entities/positions?divisionId=${division.id}`
                );
              }
              const positions = await positionsResponse.json();

              // Build division object with its positions
              divisionsWithPositions.push({
                id: division.id,
                name: division.name,
                positions: positions.map((pos) => ({
                  id: pos.id,
                  name: pos.name,
                })),
              });
            }

            // Build complete department object with nested divisions
            departmentsWithDivisions.push({
              id: department.id,
              name: department.name,
              divisions: divisionsWithPositions,
            });
          }

          // Store the completed department hierarchy for this company
          data[companyName].departments = departmentsWithDivisions;
        }

        // ====================================================================
        // STEP 3: Update state with completed data structure
        // ====================================================================
        if (!isCancelled) {
          setDropdownData(data);
          setError(null);
        }
      } catch (err) {
        // Handle any errors that occurred during the fetching process
        if (!isCancelled) {
          console.error("Error while retrieving dropdown data:", err);
          setError(err.message);
        }
      } finally {
        // Always set loading to false when done (success or failure)
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    // Start the data fetching process
    fetchDropdownData();
    // Cleanup function to prevent state updates after component unmount
    return () => {
      isCancelled = true;
    };
  }, []); // Empty dependency array - only run once on mount

  return { dropdownData, error, loading };
};

export default useDropdownData;
